/* Give the Willdesk chat a permanent home in the footer instead of a floating
 * pill over the page.
 *
 * Willdesk renders:
 *
 *   body > #__helpdesk-channel
 *            > .__iframe-container-helpdesk-channel   the chat panel, always present
 *            > .helpdesk-channel-btn-show             position:fixed, bottom/right 20px
 *                > .helpdesk-channel-start-btn        the visible pill: icon + label
 *
 * The thing that shapes this file: THE PILL IS NOT ALWAYS THERE. It is
 * rule-driven — the app keeps Willdesk_popupIntervalTime_* in localStorage and
 * a WILLDESK_RULE_EXECCONTEXT in sessionStorage, and after a visitor opens the
 * chat it stops rendering the greeting pill for a while. Observed directly: on
 * one page load the pill was present and cloneable, on the next it never
 * appeared at all across 30 seconds while the app was otherwise fully booted.
 *
 * So the footer button cannot be a clone of the pill and nothing else — that
 * would leave the footer empty exactly when the app decides not to greet. The
 * arrangement instead is:
 *
 *   - The button is built as soon as window.launchWillDeskChat exists, which is
 *     the only thing actually required to open the chat.
 *   - If the app's pill happens to be in the DOM, its contents are cloned, so
 *     the footer shows the app's real icon and its real label — which matters,
 *     because Willdesk changes that text when the team is away and a footer
 *     button promising a live reply at 3am would be a lie we wrote ourselves.
 *   - If it is not, a fallback pill is used, styled in custom.css to match.
 *   - A MutationObserver watches #__helpdesk-channel, so when the app does
 *     render its pill later, the footer button quietly upgrades itself to the
 *     real icon and the current label.
 *
 * Hiding the floating pill is gated on .willdesk-inline-ready, a class added to
 * <html> only once the footer button is actually in place and the open function
 * is confirmed. If the app is blocked, slow or restructured, the class never
 * lands and the floating pill behaves exactly as it does today. A chat widget
 * that silently becomes unreachable is worse than one in the wrong place, so
 * this degrades to "no change" rather than "no chat".
 */

(function () {
  'use strict';

  var MOUNT = '[data-chat-launcher]';
  var PILL = '.helpdesk-channel-btn-show .helpdesk-channel-start-btn';
  var READY_CLASS = 'willdesk-inline-ready';
  var FALLBACK_LABEL = 'Chat with us';

  var POLL_INTERVAL = 400; // ms — the app boots async, well after DOMContentLoaded
  var GIVE_UP_AFTER = 20000; // ms — past this, assume the app is not coming

  var button = null;

  /* Neutral pill for when the app has not rendered its own. Deliberately does
     not claim the team is online — we only say that when the app says it. */
  function fallbackPill() {
    var pill = document.createElement('span');
    pill.className = 'footer-chat-launcher__pill';
    pill.innerHTML =
      '<svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true" focusable="false">' +
      '<path fill="currentColor" d="M12 3c-4.97 0-9 3.36-9 7.5 0 2.3 1.25 4.36 3.2 5.73L5.5 20l3.94-2.05c.8.2 1.66.3 2.56.3 4.97 0 9-3.36 9-7.5S16.97 3 12 3z"/>' +
      '</svg>' +
      '<span>' + FALLBACK_LABEL + '</span>';
    return pill;
  }

  /* Copy the app's own pill in, so the footer shows its icon and its current
     label. Returns false when the app has not rendered one. */
  function adoptAppPill() {
    var source = document.querySelector(PILL);
    if (!source || !button) return false;

    var clone = source.cloneNode(true);

    /* The app writes visibility:hidden / opacity:0 inline on its pill while the
       chat panel is open. That is right for a floating launcher getting out of
       the panel's way and wrong for a footer button, which would leave a hole
       in the column. custom.css overrides it, but strip it here too so the
       clone does not start life invisible. */
    clone.style.removeProperty('visibility');
    clone.style.removeProperty('opacity');

    var label = (source.textContent || '').replace(/\s+/g, ' ').trim();
    button.setAttribute('aria-label', label || FALLBACK_LABEL);

    button.textContent = '';
    button.appendChild(clone);
    button.setAttribute('data-adopted', '');
    return true;
  }

  function build() {
    var mount = document.querySelector(MOUNT);
    if (!mount) return true; // no mount point on this page — stop polling
    if (button) return true; // already built

    if (typeof window.launchWillDeskChat !== 'function') return false;

    button = document.createElement('button');
    button.type = 'button';
    button.className = 'footer-chat-launcher__button';
    button.setAttribute('aria-label', FALLBACK_LABEL);

    button.addEventListener('click', function () {
      try {
        window.launchWillDeskChat();
      } catch (error) {
        /* If the app's own entry point throws, put its floating pill back so the
           visitor is not left without a way in. */
        document.documentElement.classList.remove(READY_CLASS);
      }
    });

    if (!adoptAppPill()) button.appendChild(fallbackPill());

    mount.appendChild(button);
    mount.hidden = false;
    document.documentElement.classList.add(READY_CLASS);

    watchForAppPill();
    return true;
  }

  /* The pill is rule-driven, so it can appear long after the button is built.
     When it does, upgrade to the real icon and the current label.

     This watches document.body, not the app's own container. The button is
     built the moment launchWillDeskChat exists, which is often before the app
     has inserted #__helpdesk-channel at all — an observer bound to that
     container would silently never attach, which is exactly the bug this
     replaced. */
  function watchForAppPill() {
    if (!window.MutationObserver) return;

    var observer = new MutationObserver(function () {
      if (!button || button.hasAttribute('data-adopted')) return;
      if (adoptAppPill()) observer.disconnect();
    });

    observer.observe(document.body, { childList: true, subtree: true });
  }

  if (build()) return;

  var startedAt = Date.now();
  var timer = setInterval(function () {
    if (build() || Date.now() - startedAt > GIVE_UP_AFTER) clearInterval(timer);
  }, POLL_INTERVAL);
})();
